import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Shield, X, Save } from "lucide-react";

const ROLES = [
  { k: "company_admin", label: "Company Admin", desc: "Full access — uploads data, manages users, publishes dashboards." },
  { k: "analyst", label: "People Analyst", desc: "Creates dashboards and uses AI. Cannot manage users." },
  { k: "leader", label: "Leader / HRBP", desc: "Views published dashboards for their business unit." },
  { k: "viewer", label: "Viewer", desc: "Views dashboards they are invited to." },
];

export default function Members() {
  const [members, setMembers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/company/members"); setMembers(data.members || []); }
    catch { toast.error("Failed to load"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-5xl mx-auto p-6 lg:p-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label-caps mb-2">Admin · Access</div>
          <h1 className="font-display text-4xl font-medium tracking-tight text-slate-900">Members & Roles</h1>
          <p className="text-slate-500 mt-3 max-w-lg">Everyone in your PeopleIQ workspace. Assign roles to control what they can do.</p>
        </div>
        <button onClick={() => setShowInvite(true)} data-testid="invite-btn" className="btn-primary"><UserPlus size={14} /> Invite member</button>
      </div>

      {loading ? <div className="text-slate-400">Loading…</div> : (
        <div className="card-flush overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-[var(--border)]">
              <tr>
                <th className="text-left px-5 py-3 label-caps font-medium">Name</th>
                <th className="text-left px-5 py-3 label-caps font-medium">Email</th>
                <th className="text-left px-5 py-3 label-caps font-medium">Role</th>
                <th className="text-left px-5 py-3 label-caps font-medium">Joined</th>
              </tr>
            </thead>
            <tbody>
              {members.map(m => (
                <tr key={m.id} className="border-b border-slate-100" data-testid={`user-${m.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{m.name}</td>
                  <td className="px-5 py-3 mono text-xs text-slate-500">{m.email}</td>
                  <td className="px-5 py-3"><span className="chip chip-primary capitalize">{m.role.replace("_", " ")}</span></td>
                  <td className="px-5 py-3 text-xs text-slate-500">{m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showInvite && <InviteDialog onClose={() => setShowInvite(false)} onSaved={() => { setShowInvite(false); load(); }} />}
    </div>
  );
}

function InviteDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", role: "viewer", password: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password min 8 chars");
    setSaving(true);
    try { await api.post("/company/invite", form); toast.success(`${form.name} invited`); onSaved(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-[var(--border)] max-w-lg w-full shadow-xl">
        <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
          <div>
            <div className="label-caps mb-1">Invite</div>
            <h2 className="font-display text-xl font-medium">Add someone to your workspace</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="invite-form">
          <div><label className="label-caps block mb-2">Email</label>
            <input data-testid="invite-email" type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required className="input-base" placeholder="user@company.com" /></div>
          <div><label className="label-caps block mb-2">Name</label>
            <input data-testid="invite-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required className="input-base" /></div>
          <div><label className="label-caps block mb-2">Initial password (share securely)</label>
            <input data-testid="invite-password" type="text" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} required minLength={8} className="input-base mono" placeholder="Min 8 characters" /></div>
          <div>
            <label className="label-caps block mb-2">Role</label>
            <div className="space-y-2">
              {ROLES.map(r => (
                <label key={r.k} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.role === r.k ? "border-[var(--primary)] bg-[var(--primary-soft)]" : "border-[var(--border)] hover:bg-slate-50"}`}>
                  <input type="radio" name="role" checked={form.role === r.k} onChange={() => setForm({...form, role: r.k})} data-testid={`role-${r.k}`} className="mt-1" />
                  <div>
                    <div className="text-sm font-medium text-slate-900 flex items-center gap-2">{r.label} {r.k === "company_admin" && <Shield size={12} className="text-[var(--primary)]" />}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{r.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </form>
        <div className="p-6 border-t border-[var(--border)] flex justify-end gap-3 bg-slate-50/50">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={saving} className="btn-primary" data-testid="invite-submit"><Save size={14} /> {saving ? "Sending…" : "Add member"}</button>
        </div>
      </div>
    </div>
  );
}
